export function isValidVariableName(name) {
    const reservedWords = new Set([
        "break", "case", "catch", "class", "const", "continue", "debugger", "default",
        "delete", "do", "else", "export", "extends", "finally", "for", "function",
        "if", "import", "in", "instanceof", "new", "return", "super", "switch", "this",
        "throw", "try", "typeof", "var", "void", "while", "with", "yield", "let", "enum",
        "await", "implements", "package", "protected", "static", "interface", "private", "public"
    ]);

    if (reservedWords.has(name)) {
        return false;
    }

    const validNameRegex = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

    return validNameRegex.test(name);
}


export function validateVariableValue(input, type) {

    const validateValue = {
        'STRING': (str = '') => {
            return /^(['"])(?:\\.|(?!\1).)*\1$/.test(str);
        },

        'INTEGER': (str = '') => {
            if(!str) return false;
            
            for(let i=0; i<str.length; i++) 
                if(!"1234567890".includes(str[i])) return false;
            return true;
        },

        'DOUBLE': (str = '') => {
            const arr = str.split('.');
            if(arr.length > 2) return false;
            
            return (
                validateVariableValue(arr[0] || '0', 'INTEGER') && 
                validateVariableValue(arr[1] ?? '0', 'INTEGER')
            )
        }
    }[type]
    
    input = input.trim();

    if(!type.includes(' ')) 
        return validateValue?.(input);

    if(input.at(0) !== '[' || input.at(-1) !== ']') return false;
    const arr = input.slice(1, input.length-1).split(',').map(n => n.trim());

    return arr.every(n => validateVariableValue(n, type.split(' ')[0]));
}


export function parseJsValue(str) {
    if (typeof str !== 'string') {
      throw new TypeError('Input must be a string');
    }
  
    if (!/^["'[{0-9-]/.test(str.trim())) {
        str = JSON.stringify(str);
    }

    try {
      return new Function(`return (${str});`)();
    } catch (err) {
      throw new Error('Invalid JS value string: ' + err.message);
    }
}
  

export function toJsString(value) {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'string') {
        const escaped =  value
            .replace(/\\/g, '\\\\')
            .replace(/\t/g, '\\t')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r')
            .replace(/"/g, '\\"')
            .replace(/'/g, "\\'");
            
        return `"${escaped}"`;
    }
  
    if (Array.isArray(value)) {
      return `[${value.map(v => toJsString(v)).join(', ')}]`;
    }
  
    if (typeof value === 'object') {
      const entries = Object.entries(value).map(([k, v]) => {
        const keyNeedsQuotes = !/^[$A-Z_][0-9A-Z_$]*$/i.test(k);
        const key = keyNeedsQuotes ? `'${k}'` : k;
        return `${key}: ${toJsString(v)}`;
      });
      return `{${entries.join(', ')}}`;
    }
  
    if (typeof value === 'function') {
      return value.toString();
    }
  
    throw new TypeError(`Unsupported type: ${typeof value}`);
}
  

export function getProgressPercentage(completed, total = 0, roundTo = 2) {
    if (total === 0) return 100;
    return Math.min((completed / total) * 100, 100).toFixed(roundTo);
}


export function formatNumberForUI(num, minLen = 4, toFix = 2) {
    if (num === undefined || num === null || isNaN(Number(num))) {
        return '0.00';
    }

    const absNum = Math.abs(num);

    let sign = num < 0 ? '-' : '';

    if (Math.floor(absNum).toString().length <= minLen) 
        return `${sign}${absNum.toFixed(toFix)}`;

    if (Math.floor(absNum / 1_000).toString().length <= minLen) 
        return `${sign}${(absNum / 1_000).toFixed(toFix)}K`;

    if (Math.floor(absNum / 100_000).toString().length <= minLen) 
        return `${sign}${(absNum / 100_000).toFixed(toFix)}M`;

    if (Math.floor(absNum / 100_00_000).toString().length <= minLen) 
        return `${sign}${(absNum / 100_00_000).toFixed(toFix)}B`;

    return `${sign}${(absNum / 100_00_000_000).toFixed(toFix)}T`;
}


export function formatFileSizeForUI(bytes, decimals = 2) {
    if (!bytes || bytes < 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(decimals)} ${units[i]}`;
}


export function rgbaToHex(r, g, b, a=1) {
    const toHex = (c) => c.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(Math.round(a * 255))}`;
}


export function compareDateObj(date1, date2) {
    if (!date2) {
        const time = new Date();
        date2 = { year: time.getFullYear(), month: time.getMonth(), date: time.getDate() };
    }

    const { date: d1, month: m1, year: y1 } = date1;
    const { date: d2, month: m2, year: y2 } = date2;

    if (y1 < y2) { return -1; }
    if (y1 > y2) { return 1; }

    if (m1 < m2) { return -1; }
    if (m1 > m2) { return 1; }

    if (d1 < d2) { return -1; }
    if (d1 > d2) { return 1; }

    return 0;
}

export function sliceFileName(name = '', len = 8) {
    if (name.length <= len) return name;
    
    const mid = Math.floor(len / 2);
    return name.slice(0, mid) + '...' + name.slice(-mid);
}


export function sliceStringForUI(str, maxLen = 15) {
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen) + '...';
}